import React from 'react';
import { object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import getCountryCodes from '../../translations/countryCodes';

import css from './BillingDetailsSummary.css';

// Read-only card of the customer's billing profile (privateData.billing):
// who is buying, where, and the VAT or registration number when set. The
// page toggles between this and BillingDetailsForm.
const countryName = (intl, code) => {
  const match = getCountryCodes(intl.locale).find(c => c.code === code);
  return match ? match.name : code;
};

// The VIES answer for the customer's VAT number, when the caller has one
// (the offer confirm step and the checkout; the billing page has none).
const VALIDATION_BADGE = {
  VERIFIED: 'BillingDetailsSummary.vatVerified',
  UNVERIFIED: 'BillingDetailsSummary.vatUnverified',
};

const BillingDetailsSummaryComponent = props => {
  const { className, billing, vatValidation, intl } = props;
  const b = billing || {};
  const badgeId = VALIDATION_BADGE[vatValidation] || null;
  const isBusiness = b.customerType === 'BUSINESS';
  const country = countryName(intl, b.country);
  const address = isBusiness
    ? [b.street, [b.postalCode, b.city].filter(Boolean).join(' '), country]
        .filter(Boolean)
        .join(', ')
    : [b.postalCode, country].filter(Boolean).join(', ');

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.nameLine}>
        {isBusiness ? (
          <>
            <span className={css.name}>{b.legalName}</span>
            <span className={css.type}>
              <FormattedMessage id="BillingDetailsSummary.business" />
            </span>
          </>
        ) : (
          <span className={css.name}>
            <FormattedMessage id="BillingDetailsSummary.consumer" />
          </span>
        )}
      </div>
      <div className={css.line}>{address}</div>
      {isBusiness && b.vatNumber ? (
        <div className={css.line}>
          <FormattedMessage
            id="BillingDetailsSummary.vatNumber"
            values={{ vatNumber: b.vatNumber }}
          />
          {badgeId ? (
            <span
              className={classNames(css.badge, {
                [css.badgeUnverified]: vatValidation === 'UNVERIFIED',
              })}
            >
              <FormattedMessage id={badgeId} />
            </span>
          ) : null}
        </div>
      ) : null}
      {isBusiness && !b.vatNumber && b.businessProofRef ? (
        <div className={css.line}>
          <FormattedMessage
            id="BillingDetailsSummary.registrationNumber"
            values={{ number: b.businessProofRef }}
          />
        </div>
      ) : null}
    </div>
  );
};

BillingDetailsSummaryComponent.defaultProps = {
  className: null,
  billing: null,
  vatValidation: null,
};
BillingDetailsSummaryComponent.propTypes = {
  className: string,
  billing: object,
  vatValidation: string,
  intl: intlShape.isRequired,
};

const BillingDetailsSummary = injectIntl(BillingDetailsSummaryComponent);
BillingDetailsSummary.displayName = 'BillingDetailsSummary';
export default BillingDetailsSummary;
